import React, { useContext, useEffect, useState } from 'react';
import { FireDangerDay } from './components/FireDangerDay';
import { DetailedFireDangerDay } from './components/DetailedFireDangerDay';
import OvalDisplay from 'components/OvalDisplay';
import moment from 'moment';
import { capitalizeWord } from 'utils/stringUtils';
import { FaFireFlameSimple } from 'react-icons/fa6';
import { FaFireAlt } from 'react-icons/fa';
import { BsChevronBarLeft, BsChevronLeft, BsChevronRight, BsFire } from 'react-icons/bs';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import { useAPIData } from 'hooks/useAPIData';
import DropdownSelector from 'components/DropdownSelector';
import { Range } from 'utils/mathUtils';
import { ColorContext, LayoutSizeContext, UniqueWidgetRefreshContext } from 'App';
import Cookies from 'js-cookie';
import useDataRefresh from 'hooks/useDataRefresh';
import { getDetailedFireDanger, getExtendedFireDanger } from 'services/weather/fireDanger.service';

let dataCache = { detailedFireDanger: null, extendedFireDanger: null };

export const FireDanger = ({
  widget,
  widgetRefresh,
  setWidgetRefresh,
  dimensions,
  containerSizePx,
}) => {
  const [fetching, setFetching] = useState(true);
  const [fireDangerData, setFireDangerData] = useState([]);
  const [detailedFireDangerData, setDetailedFireDangerData] = useState([]);
  const [selectedDay, setSelectedDay] = useState(0);
  const [error, setError] = useState({ state: false, message: '' });
  const [selected, setSelected] = useState(0);
  const [options, setOptions] = useState();
  const [detailedDates, setDetailedDates] = useState([]);

  const FusionData = useAPIData({
    successCallback: () => {
      setFetching(false);
    },
    errorCallback: () => {
      setError({ state: true, message: 'Error fetching data' });
      setFetching(false);
    },
    fetchingCallback: () => {
      setFetching(true);
    },
  });
  const uwf = useContext(UniqueWidgetRefreshContext);
  const ROUTE = 'getExtendedFireDanger';
  const LOCATION = Cookies.get('city') + '-' + Cookies.get('state');
  const UNIQUEKEY = LOCATION + '-' + ROUTE;

  useEffect(() => {
    const fetchData = async () => {
      try {
        FusionData.forceGet({
          key: ROUTE,
          apiFunction: getExtendedFireDanger,
        }).then((res) => {
          if (!res.data || !res.data.result) {
            setError({ state: true, message: 'No data found' });
            setFetching(false);
            return;
          }
          dataCache.extendedFireDanger = res.data.result;

          const fdOptions = [];
          for (let i = 0; i < dataCache.extendedFireDanger.length; i++) {
            if (!fdOptions.includes(dataCache.extendedFireDanger[i].city_id))
              fdOptions.push(dataCache.extendedFireDanger[i].city_id);
          }
          setOptions(fdOptions);

          setFireDangerData(dataCache.extendedFireDanger);
          FusionData.forceGet({
            key: 'getDetailedFireDanger',
            apiFunction: getDetailedFireDanger,
          }).then((res) => {
            const timeOfDay = ['Morning', 'Afternoon', 'Evening', 'Night'];
            let days = { 0: [], 1: [] };
            let max = 8;
            for (let i = 0; i < max; i++) {
              let thisDay = [timeOfDay[i % 4]];
              if (i < 4) {
                days['0'].push({ ...res.data.result[i], timeOfDay: thisDay });
              } else {
                days['1'].push({ ...res.data.result[i], timeOfDay: thisDay });
              }
            }
            dataCache.detailedFireDanger = days;
            setDetailedFireDangerData(dataCache.detailedFireDanger);
            // setDetailedFireDangerData(res.data.result)
            setFetching(false);
          });
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [uwf[UNIQUEKEY]]);

  const dataRefresh = useDataRefresh();
  useEffect(() => {
    const fetchData = async () => {
      try {
        FusionData.get({ key: ROUTE, apiFunction: getExtendedFireDanger }).then((res) => {
          if (!res.data || !res.data.result) {
            setError({ state: true, message: 'No data found' });
            setFetching(false);
            return;
          }
          dataCache.extendedFireDanger = res.data.result;

          const fdOptions = [];
          for (let i = 0; i < dataCache.extendedFireDanger.length; i++) {
            if (!fdOptions.includes(dataCache.extendedFireDanger[i].city_id))
              fdOptions.push(dataCache.extendedFireDanger[i].city_id);
          }
          setOptions(fdOptions);

          setFireDangerData(dataCache.extendedFireDanger);
          FusionData.get({
            key: 'getDetailedFireDanger',
            apiFunction: getDetailedFireDanger,
          }).then((res) => {
            const timeOfDay = ['Morning', 'Afternoon', 'Evening', 'Night'];
            let days = { 0: [], 1: [] };
            let max = 8;
            for (let i = 0; i < max; i++) {
              let thisDay = [timeOfDay[i % 4]];
              if (i < 4) {
                days['0'].push({ ...res.data.result[i], timeOfDay: thisDay });
              } else {
                days['1'].push({ ...res.data.result[i], timeOfDay: thisDay });
              }
            }
            dataCache.detailedFireDanger = days;
            setDetailedFireDangerData(dataCache.detailedFireDanger);
            // setDetailedFireDangerData(res.data.result)
            setFetching(false);
          });
        });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setFetching(false);
      }
    };

    fetchData();
  }, [dataRefresh.widgetRefresh]);

  const layout = useContext(LayoutSizeContext);
  const containerWidth = Number.isFinite(containerSizePx?.width)
    ? containerSizePx.width
    : dimensions?.[0] === 2
      ? 640
      : 320;
  const containerHeight = Number.isFinite(containerSizePx?.height)
    ? containerSizePx.height
    : dimensions?.[1] === 2
      ? 640
      : 320;
  const isWideWidget = widget && dimensions?.[0] == 2 && dimensions?.[1] == 1;
  const isSquareWidget = widget && dimensions?.[0] == 1 && dimensions?.[1] == 1;
  const isTallWidget = widget && dimensions?.[0] == 1 && dimensions?.[1] == 2;
  const widgetArrowSize = !widget
    ? 40
    : isSquareWidget
      ? containerWidth < 320
        ? 24
        : 28
      : isTallWidget
        ? containerWidth < 380
          ? 26
          : 30
        : containerWidth < 760
          ? 30
          : 36;
  const ovalIconSize = !widget
    ? 32 * layout.scaleMult
    : isSquareWidget
      ? 16
      : isTallWidget
        ? 18
        : containerWidth < 760
          ? 18
          : 22;
  const ovalTextSize = !widget
    ? layout.baseFontSize
    : isSquareWidget
      ? 11
      : isTallWidget
        ? 11
        : containerWidth < 760
          ? 12
          : 13;
  const ovalIconBoxSize = !widget
    ? 50 * layout.scaleMult
    : isSquareWidget
      ? 26
      : isTallWidget
        ? 28
        : containerWidth < 760
          ? 28
          : 32;
  const wideNumDisplayed = containerWidth < 780 ? 5 : 7;
  const squareNumDisplayed = containerWidth < 315 ? 2 : 3;
  const tallGridColumns = containerWidth < 380 ? 2 : 3;
  const tallNumDisplayed = tallGridColumns * 2;
  const wideCarouselHeight = Math.max(120, Math.min(205, Math.round(containerHeight * 0.37)));
  const squareCarouselHeight = Math.max(82, Math.min(122, Math.round(containerHeight * 0.24)));
  const tallGridHeight = Math.max(180, Math.min(315, Math.round(containerHeight * 0.4)));
  const compactWidgetInfo = widget && (containerWidth < 800 || isSquareWidget || isTallWidget);
  const ovalTimeStyle = { fontSize: ovalTextSize, lineHeight: 1.05 };
  const ovalTempStyle = { fontSize: ovalTextSize, lineHeight: 1.05 };
  const ovalIconContainerStyle = { width: ovalIconBoxSize, height: ovalIconBoxSize };
  const wideOvalStyle = {
    padding: compactWidgetInfo ? 6 : 8,
    maxWidth: '92%',
    margin: '0 auto',
  };
  const squareOvalStyle = {
    padding: 5,
    maxWidth: '90%',
    margin: '0 auto',
  };
  const tallOvalStyle = {
    padding: compactWidgetInfo ? 5 : 7,
    maxWidth: '92%',
    margin: '0 auto',
  };

  const fireIcons = {
    0: <FaFireFlameSimple size={ovalIconSize} />,
    1: <FaFireAlt size={ovalIconSize} />,
    2: <BsFire size={ovalIconSize} />,
    3: <BsFire size={ovalIconSize} />,
    4: <BsFire size={ovalIconSize} />,
  };

  useEffect(() => {
    dataCache = { detailedFireDanger: null, extendedFireDanger: null };
    setFetching(true);
  }, [widgetRefresh]);

  const trimLevel = (level) => {
    if (level.toLowerCase().includes('low')) return 'Low';
    if (level.toLowerCase().includes('moderate')) return 'Mod';
    if (level.toLowerCase().includes('high')) return 'High';
    if (level.toLowerCase().includes('extreme')) return 'Extr';
    return '???';
  };

  const theme = useContext(ColorContext);

  const selectedCityId = options?.[selected];
  const selectedCityKeys = Object.keys(fireDangerData || {}).filter(
    (key) => fireDangerData[key]?.city_id === selectedCityId,
  );
  const selectedCityLength = selectedCityKeys.length;
  const displayedCityKeys = selectedCityLength
    ? selectedCityKeys
    : Object.keys(fireDangerData || {});
  const safeSelectedDay = Math.min(selectedDay, Math.max(displayedCityKeys.length - 1, 0));
  const selectedDayKey = displayedCityKeys[safeSelectedDay];
  const selectedDayData = fireDangerData[selectedDayKey] ?? fireDangerData[0];

  useEffect(() => {
    if (selectedDay !== safeSelectedDay) setSelectedDay(safeSelectedDay);
  }, [safeSelectedDay, selectedDay]);

  const handleCitySelect = (index) => {
    setSelected(index);
    setSelectedDay(0);
  };

  const shouldDisplayIndex = (index, length, numDisplayed) => {
    if (length <= numDisplayed) return true;
    const halfNum = numDisplayed / 2;
    return Range(
      Math.max(Math.min(selectedDay, length - halfNum), halfNum) - halfNum,
      Math.min(Math.max(selectedDay, halfNum), length - halfNum) + halfNum,
      index,
    );
  };

  const adjustDay = (direction) => {
    const maxDay = Math.max(displayedCityKeys.length - 1, 0);
    if (direction === 'down') {
      if (selectedDay > 0) {
        setSelectedDay(selectedDay - 1);
      }
    } else {
      if (selectedDay < maxDay) {
        setSelectedDay(selectedDay + 1);
      }
    }
  };

  if (error.state && !fetching) {
    return (
      <ErrorMessage
        message={error.message}
        retryRoute={ROUTE}
        onRetry={() => {
          setError({ state: false, message: '' });
          setFetching(true);
        }}
      />
    );
  }

  // if (true && widget)
  if (fetching && widget)
    return <WidgetLoadingAnimation widgetName={'FireDanger'} dimensions={dimensions} />;

  if (fetching) return <WidgetLoadingAnimation />;

  if (fireDangerData && fireDangerData.length === 0) {
    return (
      <div>
        <WidgetTimeUpdate route={ROUTE} />
        <div className="p-3">No data found.</div>
      </div>
    );
  }

  const LeftArrowComponent = () => {
    return (
      <div
        className="cursor-pointer d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ opacity: selectedDay > 0 ? 1 : 0.35 }}
        onClick={() => adjustDay('down')}
      >
        <BsChevronLeft size={widgetArrowSize} />
      </div>
    );
  };

  const RightArrowComponent = () => {
    return (
      <div
        className="cursor-pointer d-flex align-items-center justify-content-center flex-shrink-0"
        style={{ opacity: selectedDay < Math.max(displayedCityKeys.length - 1, 0) ? 1 : 0.35 }}
        onClick={() => adjustDay('up')}
      >
        <BsChevronRight size={widgetArrowSize} />
      </div>
    );
  };

  if (!widget)
    return (
      <div className="d-flex flex-column h-100 w-100">
        <WidgetTimeUpdate route={ROUTE} />
        <div className="d-flex flex-column p-3 border-bottom border-light-subtle h3 fw-bold">
          Fire Danger
        </div>
        <div className="d-flex flex-column flex-lg-row justify-content-between h-100 w-100 overflow-auto">
          <div className="d-flex flex-grow-1 flex-column h-100">
            {Object.keys(detailedFireDangerData).map((key, index) => {
              return (
                <DetailedFireDangerDay
                  key={index}
                  data={detailedFireDangerData[index]}
                  index={index}
                />
              );
            })}
          </div>
          <div className="d-flex flex-column flex-grow-1 h-100 w-100 w-lg-50">
            {Object.keys(fireDangerData)
              .filter((item) => {
                if (detailedDates[1] >= fireDangerData[item].utc_time.split(' ')[0]) {
                  return false;
                } else {
                  return true;
                }
              })
              .map((key, index) => {
                // if (index > 1)
                return <FireDangerDay key={index} data={fireDangerData[key]} index={index} />;
              })}
          </div>
        </div>
      </div>
    );

  if (widget && dimensions[0] == 2 && dimensions[1] == 1 && fireDangerData.length)
    return (
      <div className="h-100 d-flex flex-column">
        <WidgetTimeUpdate route={ROUTE} />
        <DropdownSelector
          options={options}
          selectedOption={selected}
          setSelected={(index) => handleCitySelect(index)}
          triggerStyle={{ width: '100%', padding: 5 }}
          contentStyle={{ border: 'solid black 2px' }}
        />
        <div
          className="d-flex flex-row justify-content-center gap-2 w-100 px-2 pt-2 align-items-center flex-shrink-0"
          style={{ height: wideCarouselHeight }}
        >
          <LeftArrowComponent />
          {displayedCityKeys.map((key, index) => {
            const length = displayedCityKeys.length;

            if (!shouldDisplayIndex(index, length, wideNumDisplayed)) return;

            return (
              <div
                className="cursor-pointer text-center d-flex align-items-stretch"
                style={{ height: '100%', minWidth: 0, flex: `1 1 ${100 / wideNumDisplayed}%` }}
                key={index}
                onClick={() => setSelectedDay(index)}
              >
                <OvalDisplay
                  time={moment(fireDangerData[key]['utc_time']).format('MM/DD')}
                  icon={fireIcons[fireDangerData[key]['danger_level']]}
                  temp={trimLevel(fireDangerData[key]['level'])}
                  selected={selectedDay === index}
                  style={wideOvalStyle}
                  timeStyle={ovalTimeStyle}
                  tempStyle={ovalTempStyle}
                  iconStyle={ovalIconContainerStyle}
                />
              </div>
            );
          })}

          <RightArrowComponent />
        </div>
        <div className="px-2 pt-2 pb-1 flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
          <FireDangerDay
            widgetInfo
            compact={compactWidgetInfo}
            data={selectedDayData}
            index={safeSelectedDay}
          />
        </div>
      </div>
    );

  if (widget && dimensions[0] == 1 && dimensions[1] == 1 && fireDangerData.length)
    return (
      <div className="d-flex flex-column h-100">
        <WidgetTimeUpdate route={ROUTE} />
        <DropdownSelector
          options={options}
          selectedOption={selected}
          setSelected={(index) => handleCitySelect(index)}
          triggerStyle={{ width: '100%', padding: 5 }}
          contentStyle={{ border: 'solid black 2px' }}
        />
        <div
          className="d-flex flex-row justify-content-center align-items-center gap-1 w-100 px-1 pt-2 flex-shrink-0"
          style={{ height: squareCarouselHeight }}
        >
          <LeftArrowComponent />
          {displayedCityKeys.map((key, index) => {
            const length = displayedCityKeys.length;

            if (!shouldDisplayIndex(index, length, squareNumDisplayed)) return;

            return (
              <div
                className="cursor-pointer text-center d-flex align-items-stretch"
                style={{ height: '100%', minWidth: 0, flex: `1 1 ${100 / squareNumDisplayed}%` }}
                key={index}
                onClick={() => setSelectedDay(index)}
              >
                <OvalDisplay
                  time={moment(fireDangerData[key]['utc_time']).format('MM/DD')}
                  icon={fireIcons[fireDangerData[key]['danger_level']]}
                  temp={trimLevel(fireDangerData[key]['level'])}
                  selected={selectedDay === index}
                  style={squareOvalStyle}
                  timeStyle={ovalTimeStyle}
                  tempStyle={ovalTempStyle}
                  iconStyle={ovalIconContainerStyle}
                />
              </div>
            );
          })}

          <RightArrowComponent />
        </div>
        <div className="px-2 pt-2 pb-1 flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
          <FireDangerDay widgetInfo compact data={selectedDayData} index={safeSelectedDay} />
        </div>
      </div>
    );

  if (widget && dimensions[0] == 1 && dimensions[1] == 2 && fireDangerData.length)
    return (
      <div className="h-100 d-flex flex-column">
        <WidgetTimeUpdate route={ROUTE} />
        <DropdownSelector
          options={options}
          selectedOption={selected}
          setSelected={(index) => handleCitySelect(index)}
          triggerStyle={{ width: '100%', padding: 5 }}
          contentStyle={{ border: 'solid black 2px' }}
        />
        <div
          className="d-flex flex-row justify-content-between w-100 align-items-center px-1 pt-2 flex-shrink-0"
          style={{ height: tallGridHeight }}
        >
          <LeftArrowComponent />
          <div
            className="d-grid row-gap-2 column-gap-2"
            style={{
              gridTemplateColumns: `repeat(${tallGridColumns}, minmax(0, 1fr))`,
              height: '100%',
              flex: 1,
              minWidth: 0,
            }}
          >
            {displayedCityKeys.map((key, index) => {
              const length = displayedCityKeys.length;

              if (!shouldDisplayIndex(index, length, tallNumDisplayed)) return;

              return (
                <div
                  className="cursor-pointer text-center h-100"
                  key={index}
                  onClick={() => setSelectedDay(index)}
                >
                  <OvalDisplay
                    time={moment(fireDangerData[key]['utc_time']).format('MM/DD')}
                    icon={fireIcons[fireDangerData[key]['danger_level']]}
                    temp={trimLevel(fireDangerData[key]['level'])}
                    selected={selectedDay === index}
                    style={tallOvalStyle}
                    timeStyle={ovalTimeStyle}
                    tempStyle={ovalTempStyle}
                    iconStyle={ovalIconContainerStyle}
                  />
                </div>
              );
            })}
          </div>

          <RightArrowComponent />
        </div>
        <div className="px-2 pt-2 pb-1 flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
          <FireDangerDay
            widgetInfo
            data={selectedDayData}
            index={safeSelectedDay}
            vertical
            spacing={compactWidgetInfo ? 4 : 5}
            compact
          />
        </div>
      </div>
    );
};
